import { jumpMatches } from "@minga/core"
import { useEffect, useId, useMemo, useRef, useState } from "react"
import { SearchIcon } from "@/components/ui/icons"
import { Kbd } from "@/components/ui/kbd"
import { useProject } from "@/features/project/project-context"
import { cn } from "@/lib/utils"
import {
  isPaletteShortcut,
  KIND_LABEL,
  type PaletteEntry,
  paletteEntries,
  paletteKey,
  searchPageSearch,
} from "./knowledge-model"
import { useDecidedIndex } from "./use-decided"
import { useOpenSearchPage, useOpenTarget } from "./use-open-target"

/**
 * ⌘K (wave K §10.3, K0): jump to any item, goal, KPI, output, open card or decided card by ref or title, over what the
 * browser holds (the state and `DecidedIndex`); then always "Search everything for q" (the search page) and "Search
 * past discussions and runs for q" (the search page's history). Mounted once by `ProjectShell`, so it opens on every
 * project tab, the item and goal pages and the search page.
 */

/** A palette row: a thing to jump to, or one of the two search rows. */
export type PaletteRow =
  | { readonly _tag: "jump"; readonly entry: PaletteEntry }
  | { readonly _tag: "records" }
  | { readonly _tag: "history" }

/** The rows for a query: at most 8 jump rows, then the two search rows (none while the query is empty). */
export const paletteRows = (q: string, entries: ReadonlyArray<PaletteEntry>): ReadonlyArray<PaletteRow> => {
  if (q.trim() === "") return []
  return [
    ...jumpMatches(q, entries).map((entry): PaletteRow => ({ _tag: "jump", entry })),
    { _tag: "records" },
    { _tag: "history" },
  ]
}

/** ⌘K / Ctrl-K anywhere on the page toggles the palette (also from inside a text field). */
export const paletteShortcutHandler =
  (toggle: () => void) =>
  (e: {
    readonly key: string
    readonly metaKey?: boolean
    readonly ctrlKey?: boolean
    readonly shiftKey?: boolean
    readonly altKey?: boolean
    readonly preventDefault: () => void
  }): void => {
    if (!isPaletteShortcut(e)) return
    e.preventDefault()
    toggle()
  }

export const usePaletteShortcut = (toggle: () => void) => {
  const ref = useRef(toggle)
  ref.current = toggle
  useEffect(() => {
    const handler = paletteShortcutHandler(() => ref.current())
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [])
}

/** The header's search field (desktop): "Search Dictly… ⌘K", a button that opens the palette. */
export function SearchField({ projectName, onOpen }: { projectName: string; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-haspopup="dialog"
      aria-keyshortcuts="Meta+K Control+K"
      className="tap flex h-8 w-[220px] min-w-0 shrink items-center gap-2 rounded-control border border-rule bg-surface px-2.5 text-left text-[13px] text-placeholder hover:border-line-control max-xl:w-[180px] max-lg:w-8 max-lg:justify-center max-lg:px-0"
    >
      <SearchIcon />
      <span className="min-w-0 flex-1 truncate max-lg:sr-only">Search {projectName}…</span>
      <Kbd className="max-lg:hidden">⌘K</Kbd>
    </button>
  )
}

/** The phone header's search button: opens the palette full screen. */
export function SearchButton({ onOpen }: { onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-haspopup="dialog"
      aria-label="Search"
      className="tap flex size-10 items-center justify-center rounded-control text-ink hover:bg-fill"
    >
      <SearchIcon size={16} />
    </button>
  )
}

/** The palette's list, without the dialog: tests render it from a query. */
export function PaletteView({
  q,
  onQ,
  rows,
  selected,
  onSelect,
  onChoose,
  onKeyDown,
  onCancel,
  projectName,
}: {
  q: string
  onQ: (q: string) => void
  rows: ReadonlyArray<PaletteRow>
  selected: number
  onSelect: (index: number) => void
  onChoose: (row: PaletteRow) => void
  onKeyDown?: ((e: React.KeyboardEvent<HTMLInputElement>) => void) | undefined
  /** Phones: the visible way out (no Escape key, no backdrop around a full-screen palette). */
  onCancel?: (() => void) | undefined
  projectName: string
}) {
  const listId = useId()
  const optionId = (i: number) => `${listId}-${i}`
  const jumpCount = rows.filter((r) => r._tag === "jump").length
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-2 border-b border-rule px-4">
        <SearchIcon size={16} className="text-muted" />
        <input
          // biome-ignore lint/a11y/noAutofocus: the palette exists to type into
          autoFocus
          type="text"
          role="combobox"
          aria-expanded={rows.length > 0}
          aria-controls={listId}
          aria-activedescendant={rows.length > 0 ? optionId(selected) : undefined}
          aria-label={`Search ${projectName}`}
          placeholder={`Jump to a ref or title, or search ${projectName}…`}
          value={q}
          onChange={(e) => onQ(e.target.value)}
          onKeyDown={onKeyDown}
          // the one global focus ring, drawn inside the field so the dialog's edge does not clip it
          className="h-12 min-w-0 flex-1 rounded-control bg-transparent text-[15px] text-ink placeholder:text-placeholder focus-visible:outline-offset-[-3px] max-sm:text-[16px]"
        />
        <Kbd className="max-sm:hidden">esc</Kbd>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="tap -mr-2 min-h-11 shrink-0 rounded-control px-2 text-[15px] text-ink sm:hidden"
          >
            Cancel
          </button>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="px-4 py-5 text-[13px] text-muted">
          Type a ref like <span className="mono-data">APP-12</span> or words from a title.
        </p>
      ) : (
        <div
          id={listId}
          role="listbox"
          aria-label="Results"
          className="min-h-0 flex-1 overflow-y-auto py-1.5"
        >
          {jumpCount === 0 && (
            <p className="px-4 pt-1.5 pb-1 text-[12px] text-muted">
              No item, goal, output or card by that ref or title.
            </p>
          )}
          {rows.map((row, i) => (
            <div
              key={row._tag === "jump" ? row.entry.key : row._tag}
              id={optionId(i)}
              role="option"
              tabIndex={-1}
              aria-selected={i === selected}
              onMouseMove={() => onSelect(i)}
              onClick={() => onChoose(row)}
              onKeyDown={() => {}}
              className={cn(
                "flex min-h-11 cursor-pointer items-center gap-2.5 px-4 text-[13px] sm:min-h-9",
                i === selected ? "bg-fill" : "hover:bg-fill-soft",
                row._tag !== "jump" && i === jumpCount && "mt-1 border-t border-rule",
              )}
            >
              {row._tag === "jump" ? (
                <>
                  <span className="mono-data w-[68px] shrink-0 text-[12px] text-muted">
                    {KIND_LABEL[row.entry.kind]}
                  </span>
                  {row.entry.ref && <span className="mono-data shrink-0 text-ink">{row.entry.ref}</span>}
                  <span className="min-w-0 flex-1 truncate text-ink">{row.entry.title}</span>
                  {row.entry.status && (
                    <span className="shrink-0 text-[12px] text-muted">{row.entry.status}</span>
                  )}
                </>
              ) : (
                <>
                  <SearchIcon className="text-muted" />
                  <span className="min-w-0 flex-1 truncate">
                    {row._tag === "records"
                      ? "Search everything for "
                      : "Search past discussions and runs for "}
                    <span className="font-semibold">“{q.trim()}”</span>
                  </span>
                  {row._tag === "records" && <Kbd className="max-sm:hidden">⌘↵</Kbd>}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** Where focus goes when the palette closes: back where it was, to the page a jump opened, or left to a sheet. */
export type PaletteExit = "restore" | "page" | "none"

/** Focus the page a jump opened: its `h1` (or its `main`), without scrolling. */
export const focusPage = (doc: Document = document): void => {
  const target = doc.querySelector<HTMLElement>("main h1") ?? doc.querySelector<HTMLElement>("main")
  if (!target) return
  if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1")
  target.focus({ preventScroll: true })
}

/** The palette dialog: centred near the top on desktop, full screen on a phone. */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null)
  /** What had focus when the palette opened (the header button, the chat composer, …). */
  const returnTo = useRef<HTMLElement | null>(null)
  const exit = useRef<PaletteExit>("restore")
  const wasOpen = useRef(false)
  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) {
      const active = document.activeElement
      returnTo.current = active instanceof HTMLElement && active !== document.body ? active : null
      exit.current = "restore"
      dialog.showModal()
    }
    if (!open && dialog.open) dialog.close()
    if (!open && wasOpen.current) {
      // the focused input is gone before close() runs, so the browser restores nothing: do it here
      const how = exit.current
      const back = returnTo.current
      returnTo.current = null
      requestAnimationFrame(() => {
        if (how === "restore" && back?.isConnected) back.focus({ preventScroll: true })
        else if (how === "page") requestAnimationFrame(() => focusPage())
      })
    }
    wasOpen.current = open
  }, [open])
  const close = (how: PaletteExit = "restore") => {
    exit.current = how
    onClose()
  }
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop click; Escape closes the dialog natively
    <dialog
      ref={ref}
      aria-label="Search"
      onClose={() => close()}
      onClick={(event) => {
        if (event.target === event.currentTarget) close()
      }}
      className={cn(
        "m-0 max-h-none max-w-none bg-surface p-0 text-ink shadow-sheet",
        "mx-auto mt-[12dvh] flex max-h-[70dvh] w-[min(640px,calc(100vw-32px))] flex-col overflow-hidden rounded-card border border-rule",
        "max-sm:mt-0 max-sm:h-dvh max-sm:max-h-none max-sm:w-full max-sm:rounded-none max-sm:border-0",
        !open && "hidden",
      )}
    >
      {open && <PaletteBody onClose={close} />}
    </dialog>
  )
}

function PaletteBody({ onClose }: { onClose: (how?: PaletteExit) => void }) {
  const { state, model } = useProject()
  const decided = useDecidedIndex()
  const openTarget = useOpenTarget()
  const openSearch = useOpenSearchPage()
  const [q, setQ] = useState("")
  const [selected, setSelected] = useState(0)
  const entries = useMemo(() => paletteEntries(state, decided), [state, decided])
  const rows = useMemo(() => paletteRows(q, entries), [q, entries])
  const choose = (row: PaletteRow) => {
    // a jump to a page focuses that page; a sheet over this page and the search page focus themselves
    if (row._tag === "jump") {
      onClose(row.entry.target._tag === "over" ? "none" : "page")
      openTarget(row.entry.target)
    } else {
      onClose("none")
      openSearch(searchPageSearch(q, row._tag))
    }
  }
  return (
    <PaletteView
      q={q}
      onQ={(next) => {
        setQ(next)
        setSelected(0)
      }}
      rows={rows}
      selected={Math.min(selected, Math.max(0, rows.length - 1))}
      onSelect={setSelected}
      onChoose={choose}
      onCancel={() => onClose()}
      projectName={model.name}
      onKeyDown={(e) => {
        const action = paletteKey(e, Math.min(selected, rows.length - 1), rows.length)
        if (!action) return
        e.preventDefault()
        if (action._tag === "close") return onClose()
        if (action._tag === "move") return setSelected(action.to)
        if (action._tag === "records") return q.trim() ? choose({ _tag: "records" }) : undefined
        const row = rows[action.index]
        if (row) choose(row)
      }}
    />
  )
}
